import moment from 'moment';
import {
  IOutboxEmail,
  OutboxEmailDeliveryStatus,
  OutboxEmailStatus,
} from 'src/services/api/EmailOutbox/apiContracts';
import {
  IOutboxSocketIndicator,
  IPillColours,
  OutboxSocketState,
} from 'src/pages/Outbox/types';

export const OUTBOX_PAGE_SIZE: number = 100;

const GREY: IPillColours = { color: '#f0f0f0', textColor: '#666' };
const BLUE: IPillColours = { color: '#e3f2fd', textColor: '#1565c0' };
const GREEN: IPillColours = { color: '#e8f5e9', textColor: '#2e7d32' };
const AMBER: IPillColours = { color: '#fff8e1', textColor: '#8d6e00' };
const RED: IPillColours = { color: '#fdecea', textColor: '#c62828' };

const STATUS_COLOURS: Record<OutboxEmailStatus, IPillColours> = {
  Pending: GREY,
  Queued: GREY,
  Sending: BLUE,
  Sent: GREEN,
  Parked: RED,
};

const DELIVERY_STATUS_COLOURS: Record<OutboxEmailDeliveryStatus, IPillColours> =
  {
    Processed: BLUE,
    Deferred: AMBER,
    Blocked: RED,
    Dropped: RED,
    Bounced: RED,
    Delivered: GREEN,
    SpamReported: RED,
  };

const SOCKET_INDICATORS: Record<OutboxSocketState, IOutboxSocketIndicator> = {
  connecting: { label: 'Connecting…', ...GREY },
  live: { label: 'Live', ...GREEN },
  reconnecting: { label: 'Reconnecting…', ...AMBER },
  offline: { label: 'Live updates off', ...RED },
};

export const getStatusColours = (status: OutboxEmailStatus): IPillColours => {
  return STATUS_COLOURS[status] ?? GREY;
};

export const getDeliveryStatusColours = (
  deliveryStatus: OutboxEmailDeliveryStatus
): IPillColours => {
  return DELIVERY_STATUS_COLOURS[deliveryStatus] ?? GREY;
};

export const getSocketIndicator = (
  state: OutboxSocketState
): IOutboxSocketIndicator => {
  return SOCKET_INDICATORS[state];
};

// legacy/workaround: the API serialises these timestamps without a zone
// suffix, but they are stored in UTC.
export const formatOutboxTimestamp = (value: string | null): string => {
  if (!value) {
    return '—';
  }

  return moment.utc(value).local().format('MMM D, YYYY h:mm A');
};

const toEpoch = (value: string): number => {
  return moment.utc(value).valueOf();
};

const byNewestFirst = (a: IOutboxEmail, b: IOutboxEmail): number => {
  return toEpoch(b.createdOn) - toEpoch(a.createdOn);
};

export const mergeUpdatedOutboxEmails = (
  current: IOutboxEmail[],
  updates: IOutboxEmail[]
): IOutboxEmail[] => {
  const updatesById: Map<string, IOutboxEmail> = new Map(
    updates.map((email: IOutboxEmail): [string, IOutboxEmail] => [
      email.id,
      email,
    ])
  );

  const currentIds: Set<string> = new Set(
    current.map((email: IOutboxEmail): string => email.id)
  );

  const replaced: IOutboxEmail[] = current.map(
    (email: IOutboxEmail): IOutboxEmail => updatesById.get(email.id) ?? email
  );

  const unseen: IOutboxEmail[] = updates.filter(
    (email: IOutboxEmail): boolean => !currentIds.has(email.id)
  );

  return [...unseen, ...replaced].sort(byNewestFirst);
};

// Rows written since the first page loaded shift the server's offsets, so the
// next page can repeat rows the list already has.
export const appendOutboxPage = (
  current: IOutboxEmail[],
  nextPage: IOutboxEmail[]
): IOutboxEmail[] => {
  const currentIds: Set<string> = new Set(
    current.map((email: IOutboxEmail): string => email.id)
  );

  const newRows: IOutboxEmail[] = nextPage.filter(
    (email: IOutboxEmail): boolean => !currentIds.has(email.id)
  );

  return [...current, ...newRows];
};

export const describeOutboxLoadError = (response: unknown): string => {
  if (response instanceof Response && response.status === 403) {
    return 'Only Admins can view the outbox.';
  }

  return 'Could not load the outbox. Please try again.';
};
